import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FolderOpen, FileText, Sparkle, Stack, Spinner, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

export default function Workspaces() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/workspaces");
      setItems(data);
    } catch (e) {
      setLoadError(e.response?.data?.detail || "Unable to load workspaces. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/workspaces", { name: name.trim() });
      toast.success("Workspace created");
      setName(""); setCreating(false); load();
    } catch { toast.error("Failed to create"); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="workspaces-page">
      <PageHeader
        eyebrow="Projects"
        title="Workspaces"
        subtitle="Each workspace groups your workflows, deliverables and documents around a single venture or investment project."
        action={
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors card-elev"
            data-testid="workspaces-new-btn"
          >
            <Plus weight="bold" size={14} /> New workspace
          </button>
        }
      />

      {creating && (
        <form onSubmit={create} className="mt-6 bg-card border border-border rounded-xl p-4 flex items-center gap-3" data-testid="workspaces-new-form">
          <input
            autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Ghana Agribusiness Venture"
            className="flex-1 bg-background border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="workspaces-new-input"
          />
          <button type="submit" className="bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold">Create</button>
          <button type="button" onClick={() => { setCreating(false); setName(""); }} className="px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
        </form>
      )}

      {loading && (
        <div className="mt-10 inline-flex items-center gap-3 text-sm text-muted-foreground" role="status" aria-live="polite" data-testid="workspaces-loading">
          <Spinner size={16} weight="bold" className="animate-spin text-primary" />
          Loading workspaces…
        </div>
      )}
      {!loading && loadError && (
        <div className="mt-10 bg-card border border-border rounded-2xl p-8 max-w-xl" data-testid="workspaces-error" role="alert">
          <div className="inline-flex items-center gap-2 text-overline text-rose-600">
            <WarningCircle size={14} weight="bold" /> Unable to load workspaces
          </div>
          <div className="font-display text-xl mt-2 tracking-tight">{loadError}</div>
          <button
            onClick={load}
            className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90"
            data-testid="workspaces-retry"
          >
            <ArrowsClockwise size={14} weight="bold" /> Retry
          </button>
        </div>
      )}
      {!loading && !loadError && items.length === 0 && (
        <div className="mt-10 bg-card border border-border rounded-2xl p-10 sm:p-16 text-center" data-testid="workspaces-empty">
          <div className="inline-flex h-12 w-12 bg-teal-soft text-teal rounded-xl items-center justify-center mb-4 mx-auto">
            <FolderOpen size={20} weight="bold" />
          </div>
          <div className="font-display text-2xl mb-2 tracking-tight">No workspaces yet</div>
          <div className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
            Pick an objective from the dashboard or create an empty workspace to start grouping your work.
          </div>
          <Link to="/dashboard" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold card-elev">
            Choose an objective
          </Link>
        </div>
      )}

      {!loading && !loadError && items.length > 0 && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workspaces-grid">
          {items.map((w) => (
            <Link key={w.id} to={`/workspaces/${w.id}`}
              className="bg-card border border-border rounded-xl hover:card-elev transition-shadow p-6 flex flex-col" data-testid={`workspace-card-${w.id}`}>
              <div className="flex items-center gap-2 text-overline text-muted-foreground mb-3">
                <FolderOpen size={12} weight="bold" /> Workspace
              </div>
              <div className="font-display text-xl tracking-tight leading-tight mb-2">{w.name}</div>
              {w.description && <div className="text-sm text-muted-foreground mb-4 line-clamp-2">{w.description}</div>}
              <div className="mt-auto flex items-center gap-4 text-xs text-muted-foreground font-mono">
                <span className="inline-flex items-center gap-1"><Stack size={12} weight="bold" /> {w.workflow_count} workflows</span>
                <span className="inline-flex items-center gap-1"><FileText size={12} weight="bold" /> {w.deliverable_count} deliverables</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
